import { useState } from "react";
import { movieGenres, movieRegions, movieYears } from "@/mocks/movies";

export type RatingRange = "all" | "9" | "8.5" | "8";
export type MovieSortKey = "latest" | "popular" | "rating";

export interface MovieFilterValue {
  genre: "全部" | string;
  region: "全部" | string;
  year: "全部" | number;
  rating: RatingRange;
  sort: MovieSortKey;
}

interface MovieFiltersProps {
  value: MovieFilterValue;
  onChange: (value: MovieFilterValue) => void;
}

const ratingOptions: { key: RatingRange; label: string }[] = [
  { key: "all", label: "全部评分" },
  { key: "9", label: "9 分以上" },
  { key: "8.5", label: "8.5 分以上" },
  { key: "8", label: "8 分以上" },
];

const sortOptions: { key: MovieSortKey; label: string }[] = [
  { key: "latest", label: "最新上映" },
  { key: "popular", label: "按人气" },
  { key: "rating", label: "按评分" },
];

export default function MovieFilters({ value, onChange }: MovieFiltersProps) {
  const [open, setOpen] = useState(false);

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer ${
      active
        ? "bg-primary-500 font-semibold text-background-50"
        : "bg-background-100 text-foreground-700 hover:bg-background-200"
    }`;

  const rowClass = "flex flex-col gap-2 border-t border-background-200 pt-4 md:flex-row md:items-start first:border-t-0 first:pt-0";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between md:hidden cursor-pointer"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-filter-3-line text-base text-primary-600" aria-hidden="true"></i>
          筛选与排序
        </span>
        <i className={open ? "ri-arrow-up-s-line text-xl text-foreground-500" : "ri-arrow-down-s-line text-xl text-foreground-500"} aria-hidden="true"></i>
      </button>

      <div className={`${open ? "mt-4 flex" : "hidden"} flex-col gap-4 md:mt-0 md:flex`}>
        <div className={rowClass}>
          <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">类型</span>
          <div className="flex flex-wrap gap-2">
            {["全部", ...movieGenres].map((opt) => (
              <button key={opt} type="button" onClick={() => onChange({ ...value, genre: opt })} className={chipClass(value.genre === opt)}>
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className={rowClass}>
          <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">地区</span>
          <div className="flex flex-wrap gap-2">
            {["全部", ...movieRegions].map((opt) => (
              <button key={opt} type="button" onClick={() => onChange({ ...value, region: opt })} className={chipClass(value.region === opt)}>
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className={rowClass}>
          <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">年份</span>
          <div className="flex flex-wrap gap-2">
            {["全部", ...movieYears].map((opt) => (
              <button key={opt} type="button" onClick={() => onChange({ ...value, year: opt })} className={chipClass(value.year === opt)}>
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className={rowClass}>
          <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">评分</span>
          <div className="flex flex-wrap gap-2">
            {ratingOptions.map((opt) => (
              <button key={opt.key} type="button" onClick={() => onChange({ ...value, rating: opt.key })} className={chipClass(value.rating === opt.key)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={rowClass}>
          <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">排序</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((opt) => (
              <button key={opt.key} type="button" onClick={() => onChange({ ...value, sort: opt.key })} className={chipClass(value.sort === opt.key)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}